import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { AppSpinner } from '../ui';
import '../../styles/kkp-planning-page.scss';

/**
 * Menu ⋯ réservé aux import / export (et téléchargements associés).
 * Les autres actions de page passent par KkpPlanningPageActions en boutons visibles.
 * Le menu reste ouvert tant qu'une action est en cours.
 */
const KkpPlanningToolsMenu = ({ items = [] }) => {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [runningKey, setRunningKey] = useState(null);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        if (runningKey != null) return;
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && runningKey == null) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, runningKey]);

  const toggleDropdown = useCallback(() => {
    setOpen((value) => !value);
  }, []);

  const handleItemClick = useCallback(async (e, item) => {
    e.stopPropagation();
    if (runningKey || item.disabled) return;

    setRunningKey(item.key);
    try {
      await item.onClick();
    } catch {
      // handled by caller
    } finally {
      setRunningKey(null);
      setOpen(false);
    }
  }, [runningKey]);

  if (!items.length) return null;

  return (
    <div
      className={`dropdown kkp-planning-tools__dropdown${open ? ' is-open show' : ''}`}
      ref={rootRef}
    >
      <button
        type="button"
        className={`btn btn-outline-secondary btn-sm kkp-planning-tools__menu-btn${open ? ' show' : ''}`}
        onClick={toggleDropdown}
        aria-expanded={open}
        aria-label="Outils"
        title="Outils"
      >
        <Icon icon="mdi:dots-vertical" width={18} height={18} />
      </button>
      <ul
        className={`dropdown-menu dropdown-menu-end shadow-sm kkp-planning-tools__menu${open ? ' show' : ''}`}
        {...(open ? { 'data-bs-popper': 'static' } : {})}
      >
        {items.map((item) => {
          const isRunning = runningKey === item.key;
          return (
            <li key={item.key}>
              <button
                type="button"
                className="dropdown-item d-flex align-items-center gap-2"
                onClick={(e) => handleItemClick(e, item)}
                disabled={item.disabled || (runningKey != null && !isRunning)}
              >
                {isRunning ? (
                  <AppSpinner size="sm" centered={false} className="flex-shrink-0" />
                ) : item.icon ? (
                  <Icon icon={item.icon} className="flex-shrink-0" />
                ) : null}
                <span>{item.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default KkpPlanningToolsMenu;
